import { catchError } from 'rxjs/operators';
import { of, lastValueFrom } from 'rxjs';

import { getApiUrl, API_URL } from '@/utils/api';
import ApiBase from './ApiBase';
import { ForCarouselPayload } from '@/types';
import { GET_STOCK } from '@/constants/product';

export default class ProductsService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.PRODUCT);

  doRequest(url, queryParams) {
    // Compose query string from the given queryParams object
    const query = (new URLSearchParams(queryParams)).toString();
    const startTime = Date.now();
    const res = lastValueFrom(this.api.getJSON(`${ProductsService.baseUrl}/${url}?${query}`));
    const endTime = Date.now();
    const executionTime = endTime - startTime;
    console.log(`Temps d'exécution de 'doRequest ProductService': ${executionTime} ms`);
    return res;
  }

  getMenu(storeId) {
    const query = { storeId };

    return this.doRequest('topmenu/byStoreIdOrdering', query);
  }

  getCateg(id: string, storeId: string) {
    const query = { id, storeId };

    return this.doRequest('categ/byIdentifier', query);
  }

  /**
   * Retrieves a categ with its products & filters to display
   * @param {Object} opts
   * @param {string} opts.storeId
   * @param {number} opts.page
   * @param {number} opts.size
   * @param {string} opts.filter
   * @param {string} opts.categ
   * @param {number} opts.sort
   * @param {boolean} opts.ec
   * @param {string} storeIdForPriceCookie
   * @param {boolean} toPromise
   * @return {(Promise|Observable)}
   */
  getCategWithFiltersAndProducts(opts = {}, storeIdForPriceCookie = null) {
    const queryFormated = (new URLSearchParams(opts)).toString();
    let options = {
      credentials: 'include'
    } as any;
    if (storeIdForPriceCookie) {
      options = { ...options, cookies: `storeIdForPrice=${storeIdForPriceCookie};` };
    }

    return lastValueFrom(this.api.getJSON(`${ProductsService.baseUrl}/categ/productandfilterbycateg/paginate?${queryFormated}`, options));
  }

  getListingCateg(storeId: string, idLikeCateg: string, filter = '') {
    const query = { id: idLikeCateg, storeId, filter };
    return this.doRequest('categ/byIdLike', query);
  }

  /**
   * Retrieves a search result with filters to display
   * @param {Object} queryParam
   * @param {string} queryParam.storeId
   * @param {string} queryParam.searchTerm
   * @param {number} queryParam.page
   * @param {number} queryParam.size
   * @param {string} queryParam.filter
   * @param {string} queryParam.sort
   * @param {boolean} queryParam.ec
   * @param {string} storeIdForPriceCookie
   * @return {(Promise|Observable)}
   */
  getSearchProducts(queryParam = {}, storeIdForPriceCookie = null) {
    const queryFormated = (new URLSearchParams(queryParam)).toString();
    let options = {
      credentials: 'include'
    } as any;
    if (storeIdForPriceCookie) {
      options = { ...options, cookies: `storeIdForPrice=${storeIdForPriceCookie};` };
    }

    return lastValueFrom(this.api.getJSON(`${ProductsService.baseUrl}/search/searchProduct?${queryFormated}`, options));
  }

  getProduct(id: string) {
    const query = { id };

    return this.doRequest('product/with-carousel/byId', query);
  }

  getProductFilterByCateg(storeId: string, categ, page, size, filter, sort, ec = false, storeIdForPriceCookie = null) {
    const query = { storeId, page, size, filter, ec, categ, sort } as any;
    const queryFormated = (new URLSearchParams(query)).toString();
    let options = {
      credentials: 'include'
    } as any;
    if (storeIdForPriceCookie) {
      options = { ...options, cookies: `storeIdForPrice=${storeIdForPriceCookie};` };
    }

    return lastValueFrom(this.api.getJSON(`${ProductsService.baseUrl}/product/filterbycateg/paginate?${queryFormated}`, options));
  }

  getProductByProducteur(storeId: string, producteurId: string, currentId: string) {
    const query = { storeId, producteur: producteurId, not: currentId, page: 1, size: 6 };

    return this.doRequest('product/byproducteur/paginate', query);
  }

  getFilters({ storeId, categId, filter }: { storeId: string, categId: string, filter: string }) {
    const query = { storeId, categ: categId, filter };
    return this.doRequest('filter/filterbycateg/paginate', query);
  }

  getSuggest(searchTerm: string, storeId: string, isEc: boolean) {
    const query = { storeId, searchTerm, size: 5, ec: isEc };

    return this.doRequest('search/suggest', query);
  }

  /**
    * return the current selected object stock
    * @param {String} partnumber the selected object partnumber
    * @param {String} storeId the current storeId
    * @param {String} stockObject short for version 2, full for version 3, all others value call V1
    * @return v1 return an integer or undefined
    *         v2 return an object containing remainingQuantity key with the stock as value
    *         v2 return a full object containing _id, partnumber, warehouse, stock, stockOption,
    *            attenteFournisseur, availabilityDate, availability and lastUpdate
    * @return {Promise}
    */
  getStock(partnumber: string, storeId: string, stockObject: string) {
    const mode = stockObject === GET_STOCK.SHORT ? GET_STOCK.SHORT : stockObject === GET_STOCK.FULL ? GET_STOCK.FULL : '';
    const query = { partnumber, storeId };

    return this.doRequest(`stock${mode}`, query);
  }

  getProductInAlert(storeId: string) {
    const query = { storeId };

    return this.doRequest('product/inalert', query);
  }

  getFilterOfProductInAlert(storeId: string) {
    const query = { storeId };

    return this.doRequest('filter/inalert', query);
  }

  getBlogPost(site: string, categId: string) {
    const url = `${ProductsService.baseUrl}/categ/getBlogPost/${categId}/${site}.html`;

    return lastValueFrom(this.api.getJSON(url, { noJsonHeader: true, silentError: true }).pipe(
      catchError(() => of(''))
    ));
  }

  getBlogPostObject(site: string, categId: string) {
    const url = `${ProductsService.baseUrl}/categ/getBlogPostObject/${categId}/${site}.html`;

    return lastValueFrom(this.api.getJSON(url, { noJsonHeader: true, silentError: true }).pipe(
      catchError(() => of({}))
    ));
  }

  getCarouselProducts({ storeId, partnumber, requestType, filterSamePartnumbers }: ForCarouselPayload) {
    const payload = { storeId, partnumber, requestType, filterSamePartnumbers };
    return lastValueFrom(this.api.postJSON(`${ProductsService.baseUrl}/product/forCarousel`, payload, { credentials: 'include' }));
  }

  getCarouselProductsByGroupIdentifier({ storeId, groupIdentifier, nbProducts }) {
    const query = { storeId, groupIdentifier, nb: nbProducts };

    return this.doRequest('product/forCarouselWithCategName', query);
  }

  getWineboxUrl(groupIdByStore: string) {
    return lastValueFrom(this.api.getJSON(`${ProductsService.baseUrl}/categ/${groupIdByStore}/getWineboxUrl`));
  }

  getProductsForRecoProductCarousel({ storeId, groupIdentifier, howManyProducts }) {
    const query = { storeId, groupIdentifier, nb: howManyProducts };
    return this.doRequest('product/forCarouselWithCategName', query);
  }

  getAllBottlesProductAndCategForListingCU(opts = {}, storeIdForPriceCookie = null) {
    const queryFormated = (new URLSearchParams(opts)).toString();
    let options = {
      credentials: 'include'
    } as any;
    if (storeIdForPriceCookie) {
      options = { ...options, cookies: `storeIdForPrice=${storeIdForPriceCookie};` };
    }

    return this.api.getPromisedJSON(`${ProductsService.baseUrl}/product/allBottles/paginate?${queryFormated}`, options);
  }

  async getCritics(partnumber: string, lang: string) {
    if (partnumber) {
      const formatedPartnumber = partnumber.replace('/', '-');
      return await this.api.getPromisedJSON(`${ProductsService.baseUrl}/millesimai/${formatedPartnumber}/critics?lang=${lang}`);
    }
    return [];
  }
}
